import { i18n } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { makeAutoObservable } from "mobx";

import {
  AlertKind,
  apiDeletePatientsPatientId,
  apiGetAudit,
  apiGetClinicalStudies,
  apiGetHfComorbiditiesPatientId,
  apiGetHfVitalsPatientId,
  apiGetLabelstudioTaskProjectIdPpgUuid,
  apiGetMedicalReports,
  apiGetMessaging,
  apiGetMessagingPatientId,
  apiGetPatientsPatientId,
  apiGetPatientsPatientIdAlerts,
  apiGetPatientsPatientIdComputed,
  apiGetPatientsPatientIdDerived,
  apiGetPatientsPatientIdEvents,
  apiGetPatientsPatientIdHealthPro,
  apiGetPatientsPatientIdMonitoringInfo,
  apiGetSchedules,
  apiGetSchedulesCompliancePatientId,
  apiPostMessaging,
  apiPostPatientsPatientIdUser,
  apiPutAlertsMarkAllSeenPatientId,
  apiPutComputedUuid,
  apiPutDerivedUuid,
  apiPutPatientsPatientId,
  apiPutUsersResetUuid,
  apiPutUsersTemporaryPassUuid,
  AuditTables,
  client,
  ClinicalStudies,
  ComplianceResponse,
  CreateUserForExistingPatientSchema,
  DefaultClinicalPatientResponse,
  MinimalPatientResponse,
  AtrialFibrillationSchemaResponse,
  DefaultPatientAlertResponse,
  DefaultPatientEventResponse,
  DerivedDataRollingType,
  ExtractionStatus,
  FeatureFlagUpdate,
  HfTypeSchemaResponse,
  HealthProReportType,
  HeartCoreMonitoring,
  Level3ClinicalPatientResponse,
  Level3MinimalPatientResponse,
  NyhaClassSchemaResponse,
  PatientMessageResponse,
  PatientState,
  PermissionBit,
  PPGDerivedDataGroup,
  PPGRelatedQualityFlag,
  QuickLookTasks,
  UploadedBy,
  SingleMessageStatus,
  SinglePatientMessageResponse,
  UserFeatureFlag,
} from "../../api";
import { ApiConnector, RequestError } from "../../api/api-connector";
import { putUserFeatureFlag } from "../../api/user-feature-flags";
import { location } from "../../app/location";
import { FormModel } from "../../components/ui-kit/form/form-model";
import {
  hasClinicalStudiesAccess,
  hasMedicalAccess,
  hasMonitoringAccess,
  hasPermission,
} from "../../constants/permissions";
import {
  HeartCoreQuickLook,
  HeartCoreRangeMonths,
  PatientQuickLook,
} from "../../models/quick-look-analyses";
import { consentOk, isPaperConsent } from "../../utils/data-utils";
import { browserTimezone } from "../../utils/timezone";
import { Merge } from "../../utils/types";
import { assert, dateTimeOrNull } from "../../utils/utils";
import { ComputedData } from "./common/computed-data";
import { DerivedData } from "./common/derived-data";
import { MedDataSource, MergedMedData } from "./common/merged-med-data";
import { EventsDraft } from "./events/events-draft";
import { Logs } from "./logs/logs";
import { Schedules } from "./schedules";

export type PatientData = Merge<
  | Level3ClinicalPatientResponse
  | DefaultClinicalPatientResponse
  | Level3MinimalPatientResponse
  | MinimalPatientResponse
>;

export class Patient {
  loaded = false;
  error: { status: number; message: string } | null = null;
  data?: PatientData = undefined;
  schedules?: Schedules = undefined;
  heartCore?: HeartCoreInfo = undefined;
  events?: DefaultPatientEventResponse[] = undefined;
  alerts?: DefaultPatientAlertResponse[] = undefined;
  compliance?: ComplianceResponse = undefined;
  awaitingReviewCount = 0;
  comorbiditySummary?: ComorbiditySummary = undefined;
  /** The newest in-app message, disabled or not, so settings can re-enable it. */
  latestMessage?: PatientMessageResponse | null = undefined;
  /** The active message and whether it has expired, as the API judges it. */
  activeMessage?: SinglePatientMessageResponse = undefined;
  headerCollapsed = false;

  detailsForm = new FormModel();
  healthProDetailsForm = new FormModel();
  /** Outlives the Events tab, so switching tabs doesn't drop unsaved rows. */
  eventsDraft = new EventsDraft();

  driHeartCore?: PatientQuickLook = undefined;
  lvfpHeartCore?: PatientQuickLook = undefined;
  heartCoreReport?: HeartCoreQuickLook = undefined;
  hcReportPlot: HeartCoreRangeMonths = 12;
  hcReportTable: HeartCoreRangeMonths = 3;

  isHealthProPatient?: boolean = undefined;

  enrollments: Array<{
    study_acronym: ClinicalStudies;
    enrollment_uuid: string;
  }> = [];

  constructor(
    public patientId: number,
    public api: ApiConnector,
  ) {
    makeAutoObservable(this);
  }

  /** The message the patient's app currently shows, if any. */
  get shownMessage(): string | null {
    return this.activeMessage?.status === SingleMessageStatus.ok
      ? (this.activeMessage.message?.message_body ?? null)
      : null;
  }

  async init() {
    if (
      hasPermission(
        this.api.permissions,
        PermissionBit.RUN_HEART_CORE_QUICKLOOK,
      )
    ) {
      this.initHeartCore();
    }
    if (hasPermission(this.api.permissions, PermissionBit.ACCESS_LVFP)) {
      this.lvfpHeartCore = new PatientQuickLook(
        this.api,
        QuickLookTasks.lvfp_timeseries_ql,
        this.patientId,
      );
    }
    if (hasPermission(this.api.permissions, PermissionBit.ACCESS_DRI)) {
      this.driHeartCore = new PatientQuickLook(
        this.api,
        QuickLookTasks.hf_dri_timeseries_ql,
        this.patientId,
      );
    }

    try {
      await this.fetch();
    } catch (e) {
      if (e instanceof RequestError) {
        const status = e.response.status;
        this.error = {
          status,
          message: i18n._(msg`API error (${status})`),
        };
        if (e.response.status === 404) {
          return; // not a bug, don't report to Sentry
        }
      } else {
        this.error = { status: 0, message: i18n._(msg`Unexpected error`) };
      }
      throw e; // re-throw non-404 errors for Sentry via global handler
    }
  }

  initHeartCore() {
    this.heartCoreReport = new HeartCoreQuickLook(
      this.api,
      this.patientId,
      this.hcReportPlot,
      this.hcReportTable,
    );
  }

  async fetch() {
    this.data = (
      await apiGetPatientsPatientId({
        path: { patient_id: this.patientId },
      })
    ).data;

    await Promise.all([
      this.fetchSchedules(),
      this.fetchHeartCore(),
      this.fetchEnrollments(),
      this.fetchEvents(),
      this.fetchAlerts(),
      this.fetchAwaitingReview(),
      this.fetchCompliance(),
      this.fetchComorbiditySummary(),
      this.fetchMessage(),
    ]);
    this.isHealthProPatient = this.isHealthPro(this.data);

    this.loaded = true;
  }

  isHealthPro(data: PatientData) {
    const insuranceDetails = data?.["insurance_policy_details"];
    if (insuranceDetails === null || insuranceDetails === undefined) {
      return false;
    }
    return ["healthpro_id", "healthpro_diagnosis", "healthpro_physician"].every(
      (key) => insuranceDetails.hasOwnProperty(key),
    );
  }

  loadLogs(type: AuditTables) {
    const model = new Logs(
      [],
      this.api.permissions,
      this.api.requireUserUUID(),
    );

    if (type === AuditTables.users) {
      const users = this.data?.connected_users ?? [];

      void Promise.all(
        users.map((u) =>
          apiGetAudit({
            query: { audit_table: AuditTables.users, audit_item_uuid: u.uuid },
          }),
        ),
      ).then((results) => {
        model.data = results.flatMap((res) => res.data.audit_items);
        model.init();
      });
    } else {
      void apiGetAudit({
        query: { audit_table: type, audit_patient_id: this.patientId },
      }).then((res) => {
        model.data = res.data.audit_items;
        model.init();
      });
    }

    return model;
  }

  consentOk() {
    return this.data ? consentOk(this.data) : false;
  }

  isPaperConsent() {
    return this.data ? isPaperConsent(this.data) : false;
  }

  /**
   * Nulls are dropped, so a partial update never unsets a field it didn't mean
   * to. `clearableKeys` opts individual fields out of that: list a field here
   * when the caller distinguishes "unchanged" from "explicitly cleared" and the
   * null must reach the API (e.g. removing a medication's end date).
   */
  sanitizeBody(body: Record<string, unknown>, clearableKeys?: string[]) {
    for (const key in body) {
      if (body.hasOwnProperty(key)) {
        if (body[key] === "") {
          body[key] = null;
        }
      }
    }
    // remove null
    body = Object.fromEntries(
      Object.entries(body).filter(
        ([key, value]) => value !== null || clearableKeys?.includes(key),
      ),
    );
    return body;
  }

  async updatePatientField(body: Record<string, unknown>, sanitize = true) {
    if (sanitize) {
      body = this.sanitizeBody(body);
    }
    await apiPutPatientsPatientId({
      path: { patient_id: this.patientId },
      body,
    });
    await this.fetch();
  }

  async updateItem(
    endpoint: string,
    uuid: string,
    body: Record<string, unknown>,
    opts?: { clearableKeys?: string[] },
  ) {
    const cleanBody: any = this.sanitizeBody(body, opts?.clearableKeys);
    await client.put({
      url: `/api/v1/${endpoint}/${uuid}`,
      body: cleanBody,
    });
  }

  /**
   * Always creates a new message, even to only toggle `active`: the API then
   * deactivates the previous one, and unlike PUT (author or admin only) it is
   * open to every medical user.
   */
  async saveMessage(body: string, active: boolean) {
    await apiPostMessaging({
      body: {
        patient_id: this.patientId,
        message_body: body,
        message_active: active,
      },
    });
    await this.fetchMessage();
  }

  async overrideValue(value: number, uuid: string, dataSource: MedDataSource) {
    if (dataSource === MedDataSource.PPGDerived) {
      await apiPutDerivedUuid({
        path: { uuid: uuid },
        body: {
          seerlinq_measurement_quality_flag:
            PPGRelatedQualityFlag.manual_value_override,
          override_value: value,
        },
      });
    } else if (dataSource === MedDataSource.SQAlgo) {
      await apiPutComputedUuid({
        path: { uuid: uuid },
        body: {
          seerlinq_measurement_quality_flag:
            PPGRelatedQualityFlag.manual_value_override,
          override_value: value,
        },
      });
    }
  }

  async deleteItem(endpoint: string, uuid: string) {
    await client.delete({
      url: `/api/v1/${endpoint}/${uuid}`,
    });
  }

  async createConnectedUser(body: CreateUserForExistingPatientSchema) {
    await apiPostPatientsPatientIdUser({
      path: { patient_id: this.patientId },
      body,
    });
    this.loaded = false;
    await this.fetch();
    this.loaded = true;
  }

  /**
   * The API allows this for a non-admin caller with medical-update permission
   * and visibility over the patient - see {@link putUserFeatureFlag} for what
   * the payload may carry. The connected user in `data` is patched in place
   * instead of refetching the whole patient.
   */
  async updateConnectedUserFlag(
    uuid: string,
    flag: UserFeatureFlag,
    update: FeatureFlagUpdate,
  ) {
    const flags = await putUserFeatureFlag(uuid, flag, update);
    const user = this.data?.connected_users?.find((item) => item.uuid === uuid);
    if (user) {
      user.feature_flags = flags;
    }
  }

  async resetConnectedUserPassword(uuid: string) {
    await apiPutUsersResetUuid({
      path: { uuid },
      query: { reset_email: true },
    });
  }

  async issueTemporaryPassword(uuid: string) {
    const response = await apiPutUsersTemporaryPassUuid({
      path: { uuid },
      query: { token_expiry_timedelta: "P2D" },
    });
    return response.data.temporary_password;
  }

  async togglePatientState() {
    assert(this.data, "Patient data not loaded");

    const newState =
      this.data.patient_state === PatientState.normal
        ? PatientState.high_risk
        : PatientState.normal;
    await apiPutPatientsPatientId({
      path: { patient_id: this.patientId },
      body: { patient_state: newState },
    });
    location.reload();
  }

  async generateHealthProReports(
    reportType: HealthProReportType,
    maxReports: number,
    fromDate: string | null,
  ) {
    const reports = await apiGetPatientsPatientIdHealthPro({
      path: { patient_id: this.patientId },
      query: {
        report_type: reportType,
        max_reports: maxReports,
        from_date: fromDate || undefined,
      },
    });
    const reportsData = reports.data;
    reportsData.from_date = dateTimeOrNull(reportsData.from_date, false);
    reportsData.from_date =
      reportsData.from_date === null ? "--" : reportsData.from_date;
    return reportsData;
  }

  async clearHealthProDetails() {
    await apiPutPatientsPatientId({
      path: { patient_id: this.patientId },
      body: { insurance_policy_details: {} },
    });
    location.reload();
  }

  async fetchVitals() {
    const vitalsResp = await apiGetHfVitalsPatientId({
      path: { patient_id: this.patientId },
    });
    // merge with SpO2 and HR from derived and HR from computed
    const derived = await apiGetPatientsPatientIdDerived({
      path: { patient_id: this.patientId },
      query: {
        rolling_type: [DerivedDataRollingType.full],
        data_groups: [PPGDerivedDataGroup.hr, PPGDerivedDataGroup.spo2],
        paginate: false,
      },
    });
    const computed = await apiGetPatientsPatientIdComputed({
      path: { patient_id: this.patientId },
      query: {
        all_versions: false,
        search_string: "heart*rate",
        paginate: false,
      },
    });
    return {
      medicalData: vitalsResp.data.medical_data,
      derivedData: derived.data.derived_data,
      computedData: computed.data.computed_data,
    };
  }

  applyVitals(
    vitals: MergedMedData,
    fetched: Awaited<ReturnType<Patient["fetchVitals"]>>,
  ) {
    vitals.data = fetched.medicalData;
    const SpO2 = new DerivedData(
      fetched.derivedData,
      this.api.permissions,
      this.api.requireUserUUID(),
    );
    SpO2.filterSpO2();
    SpO2.init();
    const HRDerived = new DerivedData(
      fetched.derivedData,
      this.api.permissions,
      this.api.requireUserUUID(),
      this.api,
      QuickLookTasks.ppg_timeseries_ql,
    );
    HRDerived.filterHR();
    HRDerived.init();

    const HRComputed = new ComputedData(
      fetched.computedData,
      this.api.permissions,
      this.api.requireUserUUID(),
      this.api,
      QuickLookTasks.ppg_timeseries_ql,
    );
    HRComputed.init();

    const varMapping = {
      "SpO2: mean": "SpO2",
      "HR: mean": "heart rate",
      "heart rate": "heart rate",
    };
    vitals.mergeWithPPGDerivedAndComputed(
      [SpO2, HRDerived],
      [HRComputed],
      "measurement_type",
      varMapping,
    );
    vitals.init();
  }

  private async fetchSchedules() {
    const schedules = await apiGetSchedules({
      query: {
        patient_ids: [this.patientId],
        schedule_type: "full_measurement",
      },
    });
    this.schedules = new Schedules(
      schedules.data.schedules,
      this.api.permissions,
      this.api.requireUserUUID(),
    );
    this.schedules.init();
  }

  // Monitoring-header data (last events, unseen alerts, compliance). Each is
  // permission-guarded and defensive: a failure leaves the field undefined so
  // the profile header degrades gracefully rather than breaking the page.
  private async fetchEvents() {
    if (!hasMedicalAccess(this.api.permissions)) {
      return;
    }
    try {
      const response = await apiGetPatientsPatientIdEvents({
        path: { patient_id: this.patientId },
      });
      this.events = response.data.events;
    } catch (e) {
      console.error("Failed to fetch patient events", e);
    }
  }

  private async fetchAlerts() {
    if (!hasPermission(this.api.permissions, PermissionBit.VIEW_ALERTS)) {
      return;
    }
    try {
      const response = await apiGetPatientsPatientIdAlerts({
        path: { patient_id: this.patientId },
      });
      this.alerts = response.data.alerts;
    } catch (e) {
      console.error("Failed to fetch patient alerts", e);
    }
  }

  // HF type + atrial fibrillation come from comorbidities; NYHA class is the
  // most recent value from vitals. Fetched together for the profile-header
  // pills. Both endpoints sit behind medical access.
  private async fetchComorbiditySummary() {
    if (!hasMedicalAccess(this.api.permissions)) {
      return;
    }
    try {
      const [comorbidities, vitals] = await Promise.all([
        apiGetHfComorbiditiesPatientId({
          path: { patient_id: this.patientId },
        }),
        apiGetHfVitalsPatientId({
          path: { patient_id: this.patientId },
        }),
      ]);
      const diagnoses = comorbidities.data.diagnoses;
      const hfType = diagnoses.find(
        (d): d is HfTypeSchemaResponse => d.diagnosis_name === "HF type",
      );
      const afib = diagnoses.find(
        (d): d is AtrialFibrillationSchemaResponse =>
          d.diagnosis_name === "Atrial fibrillation",
      );
      const nyha = vitals.data.medical_data
        .filter(
          (m): m is NyhaClassSchemaResponse =>
            m.measurement_type === "NYHA class",
        )
        .sort(
          (a, b) =>
            new Date(b.measurement_datetime).getTime() -
            new Date(a.measurement_datetime).getTime(),
        )[0];
      this.comorbiditySummary = {
        hfType: hfType ? String(hfType.diagnosis_value) : null,
        afib: afib ? String(afib.diagnosis_value) : null,
        nyhaClass: nyha ? String(nyha.measurement_value) : null,
      };
    } catch (e) {
      console.error("Failed to fetch patient comorbidity summary", e);
    }
  }

  private async fetchMessage() {
    if (!hasMedicalAccess(this.api.permissions)) {
      return;
    }
    try {
      // The single-patient endpoint skips disabled messages; the list keeps
      // them, so the body survives a disable for settings to re-enable.
      const [all, active] = await Promise.all([
        apiGetMessaging({ query: { patient_ids: [this.patientId] } }),
        apiGetMessagingPatientId({ path: { patient_id: this.patientId } }),
      ]);
      this.latestMessage =
        [...all.data.messages].sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )[0] ?? null;
      this.activeMessage = active.data;
    } catch (e) {
      console.error("Failed to fetch patient message", e);
    }
  }

  private async fetchCompliance() {
    if (!hasMonitoringAccess(this.api.permissions)) {
      return;
    }
    try {
      const response = await apiGetSchedulesCompliancePatientId({
        path: { patient_id: this.patientId },
        query: {
          timezone: browserTimezone(),
        },
      });
      this.compliance = response.data;
    } catch (e) {
      console.error("Failed to fetch patient compliance", e);
    }
  }

  private async fetchHeartCore() {
    if (
      !hasPermission(
        this.api.permissions,
        PermissionBit.RUN_HEART_CORE_QUICKLOOK,
      )
    ) {
      return;
    }

    try {
      const monitoringInfo = await apiGetPatientsPatientIdMonitoringInfo({
        path: { patient_id: this.patientId },
        // Full-measurement sessions are bucketed by local day, so the windows'
        // edges follow the viewer's timezone — as compliance does above.
        query: {
          timezone: browserTimezone(),
        },
      });
      this.heartCore = {
        ...monitoringInfo.data,
        last_ppg: dateTimeOrNull(monitoringInfo.data.last_ppg, true),
        dris: monitoringInfo.data.last_5_dris?.map(([datetime, floatVal]) => [
          dateTimeOrNull(datetime),
          parseFloat(floatVal.toFixed(1)),
        ]),
      };
    } catch (e) {
      console.error("Failed to fetch patient HeartCore info", e);
    }
  }

  // Filtering the list by extraction status is a monitoring-level read, so the
  // count is left at zero for everyone else.
  async fetchAwaitingReview() {
    if (!hasMonitoringAccess(this.api.permissions)) {
      return;
    }
    try {
      const response = await apiGetMedicalReports({
        query: {
          patient_ids: [this.patientId],
          uploaded_by: UploadedBy.patient,
          extraction_statuses: [ExtractionStatus.ready],
        },
      });
      this.awaitingReviewCount = response.data.results;
    } catch (e) {
      console.error("Failed to fetch patient uploads awaiting review", e);
    }
  }

  async fetchEnrollments() {
    if (!hasClinicalStudiesAccess(this.api.permissions)) {
      return;
    }

    const response = await apiGetClinicalStudies({
      query: { patient_ids: [this.patientId] },
    });

    this.enrollments = response.data.study_enrollments.map((enrollment) => ({
      study_acronym: enrollment.study_acronym,
      enrollment_uuid: enrollment.uuid,
    }));
  }

  async redirectToLabelStudio(ppgUuid: string) {
    const response = await apiGetLabelstudioTaskProjectIdPpgUuid({
      path: {
        project_id: parseInt(import.meta.env.VITE_LABEL_STUDIO_PROJECT_ID),
        ppg_uuid: ppgUuid,
      },
    });
    window.open(response.data.url, "_blank");
  }

  async markMedicalAlertsSeen() {
    const response = await apiPutAlertsMarkAllSeenPatientId({
      path: { patient_id: this.patientId },
      query: { alert_kinds: [AlertKind.medical] },
    });
    const numMarked = response.data.results;
    alert(
      plural(numMarked, {
        one: "# medical alert was marked as seen.",
        other: "# medical alerts were marked as seen.",
      }),
    );
    location.reload();
  }

  async markTechAlertsSeen() {
    const response = await apiPutAlertsMarkAllSeenPatientId({
      path: { patient_id: this.patientId },
      query: {
        alert_kinds: [AlertKind.technical, AlertKind.compliance],
      },
    });
    const numMarked = response.data.results;
    alert(
      plural(numMarked, {
        one: "# technical and compliance alert was marked as seen.",
        other: "# technical and compliance alerts were marked as seen.",
      }),
    );
    location.reload();
  }

  async deletePatient() {
    await apiDeletePatientsPatientId({
      path: { patient_id: this.patientId },
      query: { delete_user: true },
    });
  }
}

export interface ComorbiditySummary {
  hfType: string | null;
  afib: string | null;
  nyhaClass: string | null;
}

interface HeartCoreInfo extends HeartCoreMonitoring {
  last_ppg: string | null;
  dris?: Array<[string | null, number]>;
}
